<script setup lang="ts">
  import { computed, ref, onMounted } from 'vue'

  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'

  import { useRouter } from 'vue-router/composables'
  import useIcons from '@/composables/useIcons'
  import useCourse from '@/composables/useCourse'
  import useI18n from '@/composables/useI18n'
  import useDuration from '@/composables/useDuration'
  import useSegment from '@/composables/useSegment'
  import useGamification from '@/composables/useGamification'
  import useShopifyClient from '@/composables/useShopifyClient'
  import useCommonMixin from '@/composables/useCommonMixin'

  import CourseApi from '@/services/api/CourseApi'

  import { UserModule } from '@/store/modules/user'
  import { CourseModule } from '@/store/modules/course'

  import * as Sentry from '@sentry/vue'

  const router = useRouter()

  const { baufiUpsellImage, crownIcon, arrowRightIndicator, mediaIcon } = useIcons()
  const { course, nextEnabledModule, navigateToModule, navigateToCourseContent } = useCourse()
  const { translateString } = useI18n()
  const { formatDurationToHHMM } = useDuration()
  const { frontendCourseTrialStarted } = useSegment()
  const { trackGamificationActivity } = useGamification()
  const { handleShopifyCheckout } = useShopifyClient()
  const { hasValidUser } = useCommonMixin()

  const loadingNextAction = ref(false)
  const loadingCheckout = ref(false)

  const baufiCourseDetails = computed(() => {
    return course.value?.baufiCourseDetails
  })

  const contentDetails = computed(() => {
    return `${baufiCourseDetails.value?.numberOfLessons} ${translateString('courses.lessons')}, ${formatDurationToHHMM(baufiCourseDetails.value?.duration || 0)}h ${translateString('contents')}`
  })

  const nextModule = computed(() => {
    return nextEnabledModule.value(baufiCourseDetails.value!.id)
  })

  const nextLesson = computed(() => {
    return nextModule.value?.lessons.find((lesson) => !lesson.completed) || nextModule.value?.lessons[0]
  })

  const resetCurrentCourse = () => {
    CourseModule.setCourse(null)
  }

  const handleUpsellButtonClick = async () => {
    if (!hasValidUser.value) {
      router.push('/login')
      return
    }

    if (baufiCourseDetails.value?.purchased) {
      resetCurrentCourse()
      navigateToCourseContent(nextModule.value, nextLesson.value || null, baufiCourseDetails.value!.id, false)
    } else if (baufiCourseDetails.value?.trial) {
      resetCurrentCourse()
      navigateToModule(baufiCourseDetails.value!.id, nextModule.value, nextLesson.value || null)
    } else {
      await startTrialAndNavigateToModule()
    }
  }

  const startTrialAndNavigateToModule = async () => {
    loadingNextAction.value = true
    try {
      await CourseApi.startCourseTrial([baufiCourseDetails.value!.id])
      resetCurrentCourse()
      navigateToModule(baufiCourseDetails.value!.id, nextModule.value, nextLesson.value || null)

      frontendCourseTrialStarted(UserModule.email, baufiCourseDetails.value!.id, baufiCourseDetails.value?.title || '')
      trackGamificationActivity('COURSE_TRIAL_STARTED', {
        courseId: baufiCourseDetails.value!.id,
        moduleId: null,
        lessonId: null,
      })
      CourseModule.setBaufiCourseTrialStatus(true)
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      loadingNextAction.value = false
    }
  }

  const fetchBaufiCourseModules = async () => {
    const courseModules = CourseModule.coursesModuleMap[baufiCourseDetails.value!.id]

    if (courseModules === undefined) {
      CourseModule.getCourseModules(baufiCourseDetails.value!.id)
    }
  }

  const handleCheckout = async (variantId: string, email: string) => {
    loadingCheckout.value = true
    await handleShopifyCheckout(variantId, email)
    setTimeout(() => {
      loadingCheckout.value = false
    }, 200)
  }

  onMounted(() => {
    fetchBaufiCourseModules()
  })
</script>

<template lang="pug">
.baufi-upsell-wrapper
  .upsell-image
    img(:src='baufiUpsellImage', alt='Baufi Upsell')
    .exclusive-badge-wrapper
      .exclusive-badge-content
        SVGRenderer(:icon='crownIcon', width='16px', height='16px', :fill-color='"var(--primary-color)"')
        h5 {{ $t('baufiUpsell.exclusive') }}
  .upsell-content
    h1 {{ baufiCourseDetails?.title }}
    .upsell-text
      h4.ketch-font-bold {{ $t('baufiUpsell.startWithSkgAsABaufiBroker') }}
      h5 {{ $t('baufiUpsell.descriptionText') }}
    .course-content-summary(v-if='baufiCourseDetails')
      SVGRenderer(
        :icon='mediaIcon',
        :stroke-color='"var(--primary-foreground-color)"',
        :has-hover='false',
        width='30px',
        height='30px'
      )
      h5
        span.content-duration {{ contentDetails }}
        span {{ $t('contentPreparedByMortgageExperts') }}
    .upsell-button-wrapper(:class='{ purchased: baufiCourseDetails?.purchased }')
      KetchUpButton.tertiary(@click.native='handleUpsellButtonClick')
        LoadingSpinner(v-if='loadingNextAction')
        template(v-else)
          h5.ketch-font-bold.ketch-text-primary-foreground-color {{ $t('courses.continue') }}
      .price-cta(v-if='baufiCourseDetails?.shopifyProductVariantId && !baufiCourseDetails?.purchased')
        h2.ketch-font-bold Ab {{ baufiCourseDetails?.shopifyProductPrice }}€
        KetchUpButton.primary(
          @click.native='handleCheckout(baufiCourseDetails?.shopifyProductVariantId, UserModule.email)'
        )
          LoadingSpinner(v-if='loadingCheckout')
          template(v-else)
            h5.ketch-font-bold {{ $t('selectCourse') }}
            SVGRenderer(
              :icon='arrowRightIndicator',
              width='15px',
              height='12px',
              :fill-color='"var(--primary-foreground-color)"',
              :has-hover='false'
            )
</template>

<style lang="postcss">
  .baufi-upsell-wrapper {
    @apply ketch-flex ketch-flex-col ketch-space-y-c20 ketch-p-c10 ketch-rounded-normal ketch-bg-primary-text-color ketch-text-primary-foreground-color;
    @apply xs1:ketch-p-c15 sm:ketch-p-c20 sm:ketch-grid sm:ketch-grid-cols-2 sm:ketch-gap-x-c20 sm:ketch-space-y-0 sm:ketch-rounded-large;

    .upsell-image {
      @apply ketch-relative;

      img {
        @apply ketch-w-full ketch-h-full ketch-rounded-normal ketch-object-cover ketch-object-center;
      }

      .exclusive-badge-wrapper {
        @apply ketch-absolute ketch-top-c10 ketch-left-c10 ketch-shadow-content-shadow ketch-bg-primary-text-color;
        @apply ketch-rounded-[5px] ketch-py-c8 ketch-px-c15;

        .exclusive-badge-content {
          @apply ketch-flex ketch-items-center ketch-space-x-c10;

          h5 {
            @apply ketch-text-primary-foreground-color ketch-font-bold;
          }
        }
      }
    }

    .upsell-content {
      @apply ketch-flex ketch-flex-col ketch-space-y-c15;

      h1 {
        @apply ketch-font-bolder;
        font-family: 'Playfair Display', serif;
      }

      .upsell-text {
        @apply ketch-flex ketch-flex-col ketch-space-y-c5;
      }

      .upsell-button-wrapper {
        @apply ketch-flex ketch-flex-col ketch-space-y-c15;
        @apply md:ketch-flex-row md:ketch-items-center md:ketch-justify-between md:ketch-space-y-0;

        &.purchased {
          @apply ketch-flex-row ketch-justify-end;
        }

        button {
          @apply ketch-w-fit ketch-space-x-c10;

          &.tertiary {
            @apply hover:ketch-border-primary-foreground-color;
          }

          h5 {
            filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.15));
          }
        }

        .price-cta {
          @apply ketch-flex ketch-items-center ketch-justify-between ketch-space-x-c10 ketch-w-full;
          @apply md:ketch-justify-normal md:ketch-w-fit;
        }
      }
    }
  }
</style>
